import { useEffect, useCallback, useRef, useMemo } from "react";

import { onReport } from "@/app/api/robot/report";
import { isApp } from "@/app/bridge";
import {
  ReportSceneTypeEnum,
  ReportSourceTypeEnum,
  ReportDeviceTypeEnum,
  ReportEventEnum,
  ReportParentContentEnum,
  ReportBizTypeEnum,
  ReportKeywordContentEnum,
  ReportQueuePropsType,
  ReportPropsTypes,
} from "@/app/types/typeConfig/report";
import { createUuid } from "@/app/utils/string";
import { isMobileDevice } from "@/app/utils/device";
import { useReportStore } from "@/app/store/robot/reportSlice";
import { useCurrentLocale } from "@/app/lib/LocaleProvider";
import { useAuth } from "@/app/lib/AuthProvider";

const REPORT_CACHE_KEY = "REPORT_CACHE_INFO";
export const CF_IP_COUNTRY = "cf-ipcountry";
export const CLOUDFRONT_VIEWER_COUNTRY = "cloudfront-viewer-country";
// 开发环境避免一直重复请求
const REPORT_THRESHOLD =
  process.env.NODE_ENV === "development" ? 99999999999 : 60000;

const useReportRequestQueue = () => {
  const { isAuthenticated, user } = useAuth();
  const currentLocale = useCurrentLocale();
  const countryCode = currentLocale === "zh-CN" ? "CN" : "US";
  const {
    batchToReportQueueStore,
    replaceReportQueueStore,
    addToReportQueueStore,
    reportRequestQueue,
  } = useReportStore();

  const mergeTimerRef = useRef<NodeJS.Timeout | null>(null);

  // 缓存源和设备类型，避免每次都调用
  const source = useMemo(
    () =>
      isApp()
        ? ReportSourceTypeEnum.HANDY_APP
        : ReportSourceTypeEnum.OFFICIAL_WEBSITE,
    [],
  );

  const client = useMemo(
    () =>
      isMobileDevice() ? ReportDeviceTypeEnum.MOBILE : ReportDeviceTypeEnum.PC,
    [],
  );

  // 获取上报的公共参数
  const requireReportCommonParams = useCallback(
    (scene: ReportSceneTypeEnum) => {
      return {
        region: countryCode,
        languageCode: currentLocale,
        scene,
        source,
        client,
        userId: isAuthenticated && user?.cusId ? user?.cusId : createUuid(),
      };
    },
    [countryCode, currentLocale, source, client, isAuthenticated, user?.cusId],
  );

  // 检测时间间隔 - 使用 useCallback 包裹
  const checkInterval = useCallback(
    <T extends ReportQueuePropsType | ReportQueuePropsType[]>(data: T): T => {
      const threshold = REPORT_THRESHOLD;
      let cacheInfo: { [key: string]: number } = {};

      // 容错处理 localStorage
      try {
        const cached = localStorage.getItem(REPORT_CACHE_KEY);
        if (cached) {
          cacheInfo = JSON.parse(cached);
        }
      } catch (error) {
        console.warn("Failed to read from localStorage:", error);
      }

      // 如果是数组，过滤数组中时间间隔小于阈值的元素
      if (Array.isArray(data)) {
        return data.filter(
          (item) =>
            !item.cacheKey ||
            !cacheInfo[item.cacheKey] ||
            item.createTime - cacheInfo[item.cacheKey] > threshold,
        ) as T;
      } else {
        return !data.cacheKey ||
          !cacheInfo[data.cacheKey] ||
          data.createTime - cacheInfo[data.cacheKey] > threshold
          ? data
          : ({} as T);
      }
    },
    [],
  );
  // 将业务层的数据装饰成上报结构
  const decorateReportParams = useCallback(
    (config: ReportQueuePropsType): ReportPropsTypes => {
      const { scene } = config;
      const commonParamsProvide = requireReportCommonParams(scene);
      const params = {
        ...config,
        ...commonParamsProvide,
      };
      delete params.merge;
      return params;
    },
    [requireReportCommonParams],
  );
  // 发送单个请求
  const sendRequest = useCallback(
    (config: ReportQueuePropsType) => {
      const requestData = decorateReportParams(config);
      onReport([requestData]);
    },
    [decorateReportParams],
  ); // 合并队列中的请求参数并发送
  const mergeRequests = useCallback(() => {
    if (reportRequestQueue.length === 0) {
      return;
    }

    // 使用结构化克隆或浅拷贝，避免 JSON.parse(JSON.stringify())
    const requestsData: ReportQueuePropsType[] = [...reportRequestQueue];
    const requestList: ReportQueuePropsType[] = [];

    replaceReportQueueStore(requestList);

    const reportData = requestsData.map((config) =>
      decorateReportParams(config),
    );

    onReport(reportData)
      .then(() => {
        let cacheTypeTimeInfo: { [key: string]: number } = {};

        // 容错处理 localStorage
        try {
          const cached = localStorage.getItem(REPORT_CACHE_KEY);
          if (cached) {
            cacheTypeTimeInfo = JSON.parse(cached);
          }

          // 兜底:防止数据经常变更导致内存过载
          if (Object.keys(cacheTypeTimeInfo).length > 500) {
            cacheTypeTimeInfo = {};
          }

          requestsData.forEach((item) => {
            if (item.cacheKey) {
              cacheTypeTimeInfo[item.cacheKey] = item.createTime;
            }
          });

          localStorage.setItem(
            REPORT_CACHE_KEY,
            JSON.stringify(cacheTypeTimeInfo),
          );
        } catch (error) {
          console.warn("Failed to update localStorage:", error);
        }
      })
      .catch((error) => {
        console.error("Failed to report:", error);
      });
  }, [decorateReportParams, reportRequestQueue, replaceReportQueueStore]); // 将请求添加到队列或立即发送
  const addToReportQueue = useCallback(
    (config: ReportQueuePropsType) => {
      // 判断上次的上报的时间间隔，间隔时间小于阈值，不推送到队列中
      const data = checkInterval(config);
      if (!Object.keys(data).length) return;
      const { merge = true } = config;
      if (merge) {
        addToReportQueueStore(config);
      } else {
        sendRequest(config);
      }
    },
    [checkInterval, addToReportQueueStore, sendRequest],
  );

  const batchToReportQueue = useCallback(
    (configList: ReportQueuePropsType[]) => {
      // 判断上次的上报的时间间隔，间隔时间小于阈值，不推送到队列中
      const data = checkInterval(configList);
      batchToReportQueueStore(data);
    },
    [checkInterval, batchToReportQueueStore],
  );

  // 获取上报参数对象
  const requireReportProps = useCallback(
    ({
      bizType,
      content,
      contentId,
      contentType,
      parentContent,
      parentContentId,
      parentContentType,
      scene = ReportSceneTypeEnum.NORMAL_CONSULT,
    }: {
      bizType: ReportBizTypeEnum;
      content: string;
      contentId?: string;
      contentType: ReportKeywordContentEnum;
      parentContent?: string;
      parentContentId?: string;
      parentContentType?: ReportParentContentEnum;
      scene?: ReportSceneTypeEnum;
    }): ReportPropsTypes => {
      const commonParamsProvide = requireReportCommonParams(scene);
      const behaviorCollect: ReportPropsTypes = {
        ...commonParamsProvide,
        bizType,
        content,
        contentId,
        contentType,
        createTime: new Date().getTime(),
        eventType: ReportEventEnum.MATCH,
        parentContent,
        parentContentId,
        parentContentType,
      };
      return behaviorCollect;
    },
    [requireReportCommonParams],
  );
  useEffect(() => {
    // 如果队列为空，或者定时器已经存在，则不需要重新设置定时器
    if (!reportRequestQueue.length || mergeTimerRef.current) return;

    // 否则，在延时时间内累积请求
    mergeTimerRef.current = setTimeout(() => {
      mergeRequests();
      mergeTimerRef.current = null;
    }, 800);

    // 清理定时器（仅在组件卸载或依赖变化时）
    return () => {
      if (mergeTimerRef.current) {
        clearTimeout(mergeTimerRef.current);
        mergeTimerRef.current = null;
      }
    };
  }, [mergeRequests, reportRequestQueue.length]);
  return {
    addToReportQueue,
    batchToReportQueue,
    requireReportCommonParams,
    requireReportProps,
  };
};

export default useReportRequestQueue;
